/**
 * ProtectedRoute — guards routes that require authentication and approval.
 *
 * Redirect logic:
 *  loading              → full-page spinner
 *  not authenticated    → /login
 *  status: pending      → /pending
 *  status: rejected     → /rejected
 *  status: deactivated  → /deactivated
 *  status: approved     → render children
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { FullPageSpinner } from '@/components/common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { loading, isAuthenticated, isApproved, isPending, isRejected, isDeactivated, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullPageSpinner message="Loading your account…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isPending) {
    return <Navigate to="/pending" replace />;
  }

  if (isRejected) {
    return <Navigate to="/rejected" replace />;
  }

  if (isDeactivated) {
    return <Navigate to="/deactivated" replace />;
  }

  if (!isApproved) {
    return <Navigate to="/pending" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
